// Quelle: /opt/webgis/frontend/src/components/ladewerk/Denkloader.tsx (Stand 2026-09-29)
// Geändert: Variante "halo" (SPEC 03 §3.3), Vorgabe variante = "halo"; nichts sonst.
// ============================================================
// <Denkloader> — deterministischer CSS-Loader.
//
// Eingebaut nach dem Muster der "generative-loaders" (MIT, Kasturi Khanke).
// Nicht als Abhaengigkeit gezogen, sondern nachgebaut: gebraucht werden vier
// Varianten (matrix, scan, tiles und halo — halo ist in Handschrift die einzige,
// die gezeigt wird, E7), nicht dreissig, und die Farben muessen aus unseren Tokens kommen.
//
// Warum CSS und nicht React: die einzigen fuenf Orte, an denen ein Loader
// ueberhaupt zulaessig ist (KI-Chat, PDF-/Maengel-Import, Export,
// Erreichbarkeitsanalyse, erster Kartenaufbau), sind genau die Orte, an denen
// der Haupt-Thread blockiert ist — SSE-Chunks, PDF-Rendering, Kartenaufbau.
// Eine CSS-Animation laeuft im Compositor weiter, ein React-getriebener
// Zaehler wuerde sichtbar stocken. Deshalb emittiert das JSX NUR leere
// <i>-Knoten mit Custom Properties; die gesamte Bewegung steckt in Keyframes.
//
// DETERMINISMUS IST PFLICHT, nicht Stil: kein Math.random. Jede Geometrie ist
// eine Funktion des Index, jeder Fliesskommawert auf drei Stellen quantisiert.
// Sonst sieht derselbe Loader bei jedem Aufruf anders aus, und ein Screenshot-
// Vergleich in der Regression schlaegt bei jedem Lauf fehl.
//
// Die Endlosschleife ist nahtlos, weil die Phasenstreuung ueber NEGATIVE
// animation-delays als BRUCHTEIL derselben --dl-dauer entsteht. Absolute
// Millisekunden wuerden bei jeder Tempoaenderung die Streuung zerstoeren.
// ============================================================
import { useMemo, type CSSProperties, type ReactElement } from "react";
import "./ladewerk.css";

/** Ein CSS-Objekt, das auch Custom Properties tragen darf. */
type CssVars = CSSProperties & Record<`--${string}`, string | number>;

export type DenkloaderVariante = "matrix" | "scan" | "tiles" | "halo";

export interface DenkloaderProps {
  /** halo = 8 Punkte auf einem Ring (Vorgabe), matrix = 5x5 Punkte, tiles = 4x4 Kacheln, scan = fuellende Flaeche mit Linie. */
  variante?: DenkloaderVariante;
  /** Kantenlaenge in px. */
  groesse?: number;
  /** Geschwindigkeit; 2 = doppelt so schnell. Einzige Stellschraube. */
  tempo?: number;
  /**
   * Beschriftung fuer Hilfstechnik. OHNE label ist der Loader rein dekorativ
   * und verschwindet aus dem Zugaenglichkeitsbaum — ein anonymer Status
   * stoert dort mehr, als er hilft.
   */
  label?: string;
  /** Haelt die Schleife an, ohne sie zurueckzusetzen. */
  pausiert?: boolean;
  className?: string;
  style?: CSSProperties;
}

interface Feld {
  /**
   * Manhattan-Abstand zur Mitte (matrix/tiles) beziehungsweise Wanderstrecke
   * als Vielfaches der Kantenlaenge (scan). Traegt Groesse UND Phase.
   */
  distanz: number;
  /** Negativer Bruchteil von --dl-dauer, auf drei Stellen quantisiert. */
  delay: string;
}

/**
 * Die Geometrie. Rein aus dem Index gerechnet, damit zwei Aufrufe mit
 * denselben Argumenten Zeichen fuer Zeichen dasselbe Markup ergeben.
 * Der Bruchteil ist bewusst distanz/(maxDistanz+1), nicht distanz/maxDistanz:
 * bei -1.0 laege das aeusserste Element wieder exakt auf Phase 0 und die
 * Welle haette einen toten Ring.
 */
function bauFelder(variante: DenkloaderVariante): Feld[] {
  // halo — acht Punkte auf einem Ring. distanz = Index (Winkel = Index × 45°, im CSS),
  // Phase = −Index × 0,105 der Dauer: 0.000, −0.105 … −0.735. Bruchteil der Dauer,
  // damit die Streuung jede Tempoaenderung ueberlebt; kein Math.random.
  if (variante === "halo")
    return Array.from({ length: 8 }, (_, i) => ({ distanz: i, delay: (-(i * 0.105)).toFixed(3) }));
  if (variante === "scan") {
    // Zwei Schichten: [0] fuellt von unten (steht, waechst nur), [1] ist die
    // wandernde Linie und legt eine volle Kantenlaenge zurueck. Beide MUESSEN
    // in Phase laufen, deshalb kein Versatz.
    return [
      { distanz: 0, delay: "0.000" },
      { distanz: 1, delay: "0.000" },
    ];
  }
  if (variante === "tiles") {
    const felder: Feld[] = [];
    for (let i = 0; i < 16; i++) {
      const x = i % 4;
      const y = Math.floor(i / 4);
      // Mitte des 4x4-Rasters liegt bei 1,5 — in Halbschritten gerechnet,
      // damit die Distanz ganzzahlig bleibt: 1, 2 oder 3.
      const distanz = (Math.abs(2 * x - 3) + Math.abs(2 * y - 3)) / 2;
      felder.push({ distanz, delay: (-((distanz - 1) / 3)).toFixed(3) });
    }
    return felder;
  }
  const felder: Feld[] = [];
  for (let i = 0; i < 25; i++) {
    const x = i % 5;
    const y = Math.floor(i / 5);
    const distanz = Math.abs(x - 2) + Math.abs(y - 2);
    felder.push({ distanz, delay: (-(distanz / 5)).toFixed(3) });
  }
  return felder;
}

export function Denkloader({
  variante = "halo", // war "matrix"
  groesse = 28,
  tempo = 1,
  label,
  pausiert = false,
  className = "",
  style,
}: DenkloaderProps): ReactElement {
  const felder = useMemo(() => bauFelder(variante), [variante]);
  // Tempo 0 wuerde die Dauer durch null teilen und alles einfrieren — dafuer
  // gibt es pausiert, und das laeuft ueber animation-play-state.
  const takt = tempo > 0 ? tempo : 1;

  const wurzelStil: CssVars = {
    "--dl-groesse": `${groesse}px`,
    "--dl-tempo": takt,
    ...style,
  };

  return (
    <span
      className={`dl dl-${variante} ${className}`.trim()}
      style={wurzelStil}
      // JavaScript greift pro Bild NIRGENDS ein: das Anhalten ist ein
      // Attribut, den Rest macht animation-play-state im CSS.
      data-paused={pausiert ? "true" : undefined}
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
    >
      {felder.map((f, i) => {
        const feldStil: CssVars = {
          "--dl-i": i,
          "--dl-distanz": f.distanz,
          "--dl-delay": f.delay,
        };
        return <i key={i} style={feldStil} />;
      })}
    </span>
  );
}
